/**
 * Onboarding wizard inteligente — la primera experiencia de Carlos en CARLOS OS.
 * 16 pasos cuidadosamente diseñados para que CARLOS OS te conozca a fondo.
 * El sistema va construyendo tu perfil, contexto de negocios, finanzas, salud, IA.
 *
 * Estado: paso actual viene de /api/v1/onboarding/state
 * Persistencia: cada paso hace POST /api/v1/onboarding/step
 */
import { apiClient } from '../lib/api.js';
import { STEPS } from './steps.js';

export async function renderOnboarding(root, { user, onComplete }) {
  root.innerHTML = '';

  // Cargar estado
  const state = await apiClient.get('/api/v1/onboarding/state').catch(() => ({ currentStep: 'identity', progress: {} }));
  const stepIds = state.steps || STEPS.map((s) => s.id);

  const wizard = {
    currentIdx: Math.max(0, stepIds.indexOf(state.currentStep)),
    data: {},  // datos parciales recogidos por paso
    progress: state.progress || {},
    user,
    onComplete,
  };

  // Container principal
  const container = document.createElement('div');
  container.className = 'min-h-screen flex flex-col bg-gradient-to-br from-base-900 via-base-900 to-base-800';
  container.innerHTML = `
    <header class="border-b border-base-700 bg-base-800/60 backdrop-blur-md">
      <div class="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-lg bg-gradient-to-br from-gold-300 to-gold-500 shadow-gold flex items-center justify-center font-display font-bold text-base-900">C</div>
          <h1 class="font-display font-bold text-xl">CARLOS<span class="text-gold-400"> OS</span></h1>
        </div>
        <button id="skip-btn" class="text-xs text-ink-400 hover:text-ink-200">Saltar onboarding</button>
      </div>
      <div class="max-w-3xl mx-auto px-6 pb-3">
        <div class="flex items-center justify-between mb-1">
          <span id="step-label" class="text-xs uppercase tracking-widest text-gold-400"></span>
          <span id="step-counter" class="text-xs text-ink-400"></span>
        </div>
        <div class="h-1 bg-base-700 rounded-full overflow-hidden">
          <div id="progress-bar" class="h-full bg-gradient-to-r from-gold-400 to-gold-300 transition-all duration-500" style="width: 0%"></div>
        </div>
      </div>
    </header>

    <main class="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
      <div id="step-content" class="animate-slide-up"></div>
    </main>

    <footer class="border-t border-base-700 bg-base-800/30">
      <div class="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
        <button id="back-btn" class="text-ink-300 hover:text-ink-100 text-sm disabled:opacity-30">← Atrás</button>
        <div class="flex gap-2">
          <button id="skip-step-btn" class="text-ink-300 hover:text-ink-100 text-sm">Omitir paso</button>
          <button id="next-btn" class="bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold px-6 py-2 rounded-lg transition-all shadow-gold">Siguiente →</button>
        </div>
      </div>
    </footer>
  `;
  root.appendChild(container);

  const els = {
    label: container.querySelector('#step-label'),
    counter: container.querySelector('#step-counter'),
    bar: container.querySelector('#progress-bar'),
    content: container.querySelector('#step-content'),
    back: container.querySelector('#back-btn'),
    next: container.querySelector('#next-btn'),
    skipStep: container.querySelector('#skip-step-btn'),
    skipAll: container.querySelector('#skip-btn'),
  };

  els.back.addEventListener('click', () => prev());
  els.next.addEventListener('click', () => next());
  els.skipStep.addEventListener('click', () => next(true));
  els.skipAll.addEventListener('click', async () => {
    if (!confirm('¿Saltar todo el onboarding? Podrás rellenar tu perfil más tarde en Ajustes.')) return;
    // Si el backend está vivo, marcamos completed. Si falla, igualmente seguimos
    // adelante en el frontend para que Carlos no quede atrapado.
    els.skipAll.disabled = true;
    els.skipAll.textContent = 'Saltando…';
    try {
      await Promise.race([
        apiClient.post('/api/v1/onboarding/complete', {}),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 8000)),
      ]);
    } catch (e) {
      console.warn('[onboarding] no se pudo marcar complete en backend, sigo en cliente:', e);
    } finally {
      onComplete?.();
    }
  });

  function renderCurrent() {
    const step = STEPS[wizard.currentIdx];
    els.label.textContent = step.title;
    els.counter.textContent = `Paso ${wizard.currentIdx + 1} de ${STEPS.length}`;
    els.bar.style.width = `${((wizard.currentIdx + 1) / STEPS.length) * 100}%`;
    els.content.innerHTML = '';
    els.content.appendChild(step.render({
      data: wizard.data[step.id] || {},
      user: wizard.user,
      onChange: (d) => { wizard.data[step.id] = d; },
    }));
    els.back.disabled = wizard.currentIdx === 0;
    els.next.textContent = wizard.currentIdx === STEPS.length - 1 ? 'Terminar 🎉' : 'Siguiente →';
  }

  async function next(skip = false) {
    const step = STEPS[wizard.currentIdx];
    if (!skip && step.validate) {
      const err = step.validate(wizard.data[step.id] || {});
      if (err) {
        alert(err);
        return;
      }
    }
    els.next.disabled = true;
    els.skipStep.disabled = true;
    els.next.textContent = 'Guardando…';

    // Helper: intenta guardar en backend con timeout corto. Si falla,
    // continuamos en cliente para no bloquear al usuario.
    const tryPersist = async (path, payload) => {
      try {
        await Promise.race([
          apiClient.post(path, payload),
          new Promise((_, reject) => setTimeout(() => reject(new Error('backend timeout')), 8000)),
        ]);
        return { ok: true };
      } catch (err) {
        console.warn(`[onboarding] persist ${path} falló, sigo localmente:`, err?.message);
        return { ok: false, error: err };
      }
    };

    try {
      const persist = await tryPersist('/api/v1/onboarding/step', {
        step: step.id,
        data: skip ? {} : (wizard.data[step.id] || {}),
      });

      // Si es el último paso → marcar complete (también tolerante)
      if (wizard.currentIdx === STEPS.length - 1) {
        await tryPersist('/api/v1/onboarding/complete', {});
        onComplete?.();
        return;
      }

      // Avanzamos SIEMPRE en el cliente para que Carlos no se quede atrapado.
      // Si hubo error de red, mostramos toast suave pero continuamos.
      if (!persist.ok) {
        showToast('⚠️ Datos guardados localmente — backend no responde. Se sincronizará después.');
      }
      wizard.currentIdx += 1;
      renderCurrent();
    } catch (e) {
      // Errores inesperados (no de red): mostrar pero NO bloquear.
      console.error('[onboarding] error inesperado:', e);
      alert('Error inesperado: ' + (e.message || 'desconocido') + '\n\nPuedes seguir; el dato se guardará cuando vuelva el servidor.');
      wizard.currentIdx += 1;
      renderCurrent();
    } finally {
      els.next.disabled = false;
      els.skipStep.disabled = false;
    }
  }

  function showToast(message) {
    const t = document.createElement('div');
    t.className = 'fixed top-4 right-4 z-50 bg-gold-400/90 text-base-900 px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-sm';
    t.textContent = message;
    document.body.appendChild(t);
    setTimeout(() => { t.style.opacity = '0'; t.style.transition = 'opacity 0.4s'; }, 3500);
    setTimeout(() => t.remove(), 4000);
  }

  function prev() {
    if (wizard.currentIdx > 0) {
      wizard.currentIdx -= 1;
      renderCurrent();
    }
  }

  renderCurrent();
}
